import { useApolloClient } from "@apollo/client"
import { RouteProp } from "@react-navigation/core"
import { StackNavigationProp } from "@react-navigation/stack"
import { ErrorMessage, Form, Text, TouchButton } from "pusatec-react-native"
import React from "react"
import { goBack, MainStackParamList } from "../../MainNavigation"
import Base from "../../components/layout/Base"
import MainScrollPanel from "../../components/layout/MainScrollPanel"
import Panel from "../../components/layout/Panel"
import Duration from "../../utils/Duration"
import { useTranslation } from "react-i18next"
import { useCreateItemMutation, useUpdateItemMutation } from "./queries.generated"
import { View } from "react-native"
import { BackIcon } from "../../components/BackIcon"
import { ItemGroupSearch } from "../../components/ItemGroupSearch"
import { RichTextEditor } from "../../components/RichTextEditor"
import { CheckBox } from "react-native-elements"
import { ItemImagePicker } from "../../components/ItemImagePicker"
import { createItemImageUpload } from "../../utils/itemImage"

interface Props {
	navigation: StackNavigationProp<MainStackParamList, "CreateItem">
	route: RouteProp<MainStackParamList, "CreateItem">
}

export function CreateItemScreen(props: Props) {
	const { navigation, route } = props
	const { params: { systemId } } = route
	const { t } = useTranslation()
	const apolloClient = useApolloClient()
	const [selectedItemGroupId, setSelectedItemGroupId] = React.useState<string>("")

	const [createItem, createItemM] = useCreateItemMutation()
	const [updateItem, updateItemM] = useUpdateItemMutation()

	return (
		<Form
			initialValues={{
				rentable: false,
				needsPermit: false,
				emailAdminWhenReturned: false
			}}
			onSubmit={async values => {
				const response = await createItem({
					variables: {
						input: {
							lockersystemId: systemId,
							name: values.name,
							description: values.description,
							itemGroupId: selectedItemGroupId,
							rentingMaxTime: (values.rentingMaxTime / 1000),
							rentable: values.rentable ?? false,
							needsPermit: values.needsPermit ?? false,
							emailAdminWhenReturned: values.emailAdminWhenReturned ?? false
						}
					}
				})

				if (!response.data?.result?.item?.id) {
					return
				}

				await apolloClient.refetchQueries({
					include: ["AdminView"],
				})

				const imageUpload = createItemImageUpload(values.image)

				if (imageUpload) {
					await updateItem({
						variables: {
							input: {
								itemId: response.data.result.item.id,
								image: imageUpload,
							}
						}
					})
				}

				navigation.replace("PlaceItemToLocker", {
					itemId: response.data.result.item.id,
					itemWasJustCreated: true,
				})
			}}
			style={{ flex: 1 }}
		>
			<MainScrollPanel>
				<Base>
					<Text type="h2" text={t("Create new item")} style={{ marginBottom: 20 }} />
					<Form.TextField autoFocus name="name" label={t("Name")} required />

					<Form.DurationField
						name="rentingMaxTime"
						label={t("Renting time")}
						required
						validate={value => {
							const duration = new Duration(value ?? 0)
							if (duration.getAsUnit("minutes") < 5)
								return t("Renting time is too short")
						}}
					/>
					<Text text={t("Description")} style={{ marginTop: 20 }} />
					<Form.CustomField name="description" render={({ handleFieldValueChange, formContext }) => {
						const value = formContext.fields.description?.value ?? formContext.initialValues?.description ?? false

						return (
							<RichTextEditor
								markdown={value}
								onChange={ descriptionText => {
									handleFieldValueChange(descriptionText)
								}}
							/>
						)
					}} />
					<Form.CustomField name="image" render={({ handleFieldValueChange, formContext }) => {
						const value = formContext.fields.image?.value ?? null

						return (
							<ItemImagePicker
								value={value}
								onChange={handleFieldValueChange}
							/>
						)
					}} />
					<Form.CustomField name="rentable" render={({ handleFieldValueChange, formContext }) => {
						const value = formContext.fields.rentable?.value ?? formContext.initialValues?.rentable ?? false
						return (
							<CheckBox
								checked={value}
								title={t("Rentable")}
								onPress={() => handleFieldValueChange(!value)} />
						)
					}} />
					<Form.CustomField name="needsPermit" render={({ handleFieldValueChange, formContext }) => {
						const value = formContext.fields.needsPermit?.value ?? formContext.initialValues?.needsPermit ?? false
						return (
							<CheckBox
								checked={value}
								title={t("Needs permit")}
								onPress={() => handleFieldValueChange(!value)} />
						)
					}} />
					<Form.CustomField name="emailAdminWhenReturned" render={({ handleFieldValueChange, formContext }) => {
						const value = formContext.fields.emailAdminWhenReturned?.value ?? formContext.initialValues?.emailAdminWhenReturned ?? false
						return (
							<CheckBox
								checked={value}
								title={t("Email admin when returned")}
								onPress={() => handleFieldValueChange(!value)} />
						)
					}} />
					<Text text={t("Item groups")} style={{ marginTop: 20 }} />
					<ItemGroupSearch
						lockersystemId={systemId}
						selectedItemGroupId={selectedItemGroupId}
						onItemGroupidChanged={id => {
							if (id === selectedItemGroupId) {
								setSelectedItemGroupId("")

								return
							}

							setSelectedItemGroupId(id)
						}}
					/>
				</Base>
			</MainScrollPanel>
			<Panel>
				{createItemM.error && <ErrorMessage extra={createItemM.error} />}
				{updateItemM.error && <ErrorMessage extra={updateItemM.error} />}
				<View style={{ flexDirection: "row", justifyContent: "space-between", }}>
					<TouchButton
						text={t("Cancel")}
						icon={BackIcon}
						onPress={() => {
							goBack(navigation)
						}}
					/>
					<Form.Submit
						type="TouchButton"
						text={t("Create item")}
						loading={createItemM.loading || updateItemM.loading}
					/>
				</View>
			</Panel>
		</Form>
	)
}
